import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { PriceAlertConfigFormContent } from "./PriceAlertConfigForm";
import {
  PriceAlertConfigData,
  CreatePriceAlertConfigPayload,
  UpdatePriceAlertConfigPayload,
} from "@/api/price-alerts";

const SHEET_WIDTH = 458;

interface PriceAlertConfigSheetDesktopProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config?: PriceAlertConfigData | null;
  onSubmit: (payload: CreatePriceAlertConfigPayload | UpdatePriceAlertConfigPayload) => void;
  isLoading?: boolean;
}

export const PriceAlertConfigSheetDesktop: React.FC<PriceAlertConfigSheetDesktopProps> = ({
  open,
  onOpenChange,
  config,
  onSubmit,
  isLoading,
}) => {
  const isEdit = !!config;

  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      document.body.style.overflow = "hidden";
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      document.body.style.overflow = "";
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onOpenChange(false);
    };
    if (open) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onOpenChange]);

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <button
        type="button"
        className="fixed inset-0 bg-black/60 z-[120] transition-opacity duration-300 cursor-default border-none"
        style={{ opacity: isAnimating ? 1 : 0 }}
        onClick={() => onOpenChange(false)}
        aria-label="Close sheet"
        tabIndex={-1}
      />

      {/* Sheet */}
      <div
        className="fixed top-0 right-0 h-full bg-secondary border-l border-border flex flex-col shadow-2xl z-[121] transition-transform duration-300 ease-out"
        style={{
          width: `${SHEET_WIDTH}px`,
          transform: isAnimating ? "translateX(0)" : "translateX(100%)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <p className="text-xs text-text-secondary mb-1">
              {isEdit ? "Edit" : "New"}
            </p>
            <h2 className="text-lg font-semibold text-text-primary">
              {isEdit ? "Edit Price Alert" : "New Price Alert"}
            </h2>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="p-2 rounded-lg hover:bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <PriceAlertConfigFormContent
            config={config}
            onSubmit={onSubmit}
            isLoading={isLoading}
          />
        </div>
      </div>
    </>
  );
};
